import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components/macro'
import { useTranslation } from 'react-i18next'
import { useLocation, withRouter } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { isSubmitting } from 'redux-form'

import * as routes from 'config/routes'

import { getCarfile } from 'redux/actions/data'

import { media } from 'utilities/styled'
import { mapCarFileType } from 'utilities/mapping'
import {
  getBackendNapValue,
  getObjectFromQueryString,
  scrollToTop,
} from 'utilities/utils'

import LoadingIndicator from 'components/atoms/loading-indicator'
import PageHeader from 'components/organisms/page-header'
import CarEntryTabs from 'components/organisms/car-entry-tabs'

const Container = styled.div`
  background: ${({ theme }) => theme.colors.brandFoxtrot};
  padding: 0 20px;

  ${media.desktop`
    padding: 0 60px;
  `};
`

function CarEntryStep1Manual({
  createCarfile,
  forms,
  getForms,
  nap,
  newCar,
  updateCarfile,
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const queryParams = getObjectFromQueryString(location.search)
  const carId = queryParams && queryParams.auto_id
  const carFile = useSelector(
    (state) => state.data && state.data.carfile && state.data.carfile.data,
  )
  const carFileLoading = useSelector((state) => {
    const { data } = state
    return (
      (data.createCarfile && data.createCarfile.loading) ||
      (data.updateCarfile && data.updateCarfile.loading)
    )
  })
  const isFormSubmitting = useSelector(
    (state) => state?.form?.carEntry && isSubmitting('carEntry')(state),
  )

  const napValue = nap && nap.data && nap.data.nap
  const napBackendValue = getBackendNapValue(napValue)
  const car = {
    ...newCar,
    ...carFile,
    nap: napBackendValue,
  }

  useEffect(() => {
    if (carId && !carFile) {
      dispatch(getCarfile(carId))
    }
  }, [carFile, carId, dispatch])

  useEffect(() => {
    getForms('carfile', { kenteken: car.kenteken, auto_id: carId })
    // has no dependencies, so it will only run once on componentDidMount and
    // on at that time. This ensures that a clean version of the forms config
    // is retrieved for car entry, otherwise that forms for the last visited
    // carFile are used.
    // documented in: https://app.clickup.com/t/6unbvp
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!forms) {
      getForms('carfile', { kenteken: car.kenteken, auto_id: carId })
    }
  }, [car.kenteken, carId, forms, getForms])

  useEffect(() => {
    scrollToTop()
  }, [])

  return (
    <Container>
      <PageHeader
        buttonTo={`${routes.CAR_ENTRY}/1`}
        mainTitle={mapCarFileType(t, car?.voertuigsoort_mapped)}
        status={t('entry')}
        subTitle={t('step22AddData')}
      />

      {!forms || forms.loading || carFileLoading || isFormSubmitting ? (
        <LoadingIndicator />
      ) : (
        <CarEntryTabs
          car={car}
          createCarfile={createCarfile}
          formId="carEntry"
          forms={forms.data}
          getForms={getForms}
          manual
          updateCarfile={updateCarfile}
        />
      )}
    </Container>
  )
}

CarEntryStep1Manual.propTypes = {
  createCarfile: PropTypes.func.isRequired,
  forms: PropTypes.object,
  getForms: PropTypes.func.isRequired,
  nap: PropTypes.object,
  newCar: PropTypes.object,
  updateCarfile: PropTypes.func.isRequired,
}

CarEntryStep1Manual.defaultProps = {
  forms: null,
  nap: undefined,
  newCar: null,
}

export default withRouter(CarEntryStep1Manual)
